import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Search, ShieldCheck, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { normalizeRole } from '@/lib/permissions';
import type { ManagedRole } from '@/lib/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ConfirmActionDialog } from '@/components/ui/confirm-action-dialog';
import { PageLoading } from '@/components/ui/loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TableIndexCell, TableIndexHead } from '@/components/ui/table-index';
import { rowSerial } from '@/lib/row-serial';
import { Pagination } from '@/components/ui/pagination';
import { useClientPagination } from '@/hooks/useClientPagination';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function RolesListPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [deleteTarget, setDeleteTarget] = useState<ManagedRole | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{ item: ManagedRole; action: 'activate' | 'deactivate' } | null>(null);
  const { hasPermission, hasAnyRole } = useAuth();
  const isSystemAdmin = hasAnyRole(['ORG_ADMIN', 'Admin']);
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const { data: roles = [], isLoading, isError, error } = useQuery({
    queryKey: ['managed-roles'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: ManagedRole[] }>('/roles?includeInactive=true');
      return response.data ?? [];
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (roles ?? [])
      .filter((role) => role.name !== 'SUPER_ADMIN')
      .filter((role) => {
        const matchesText = !q || role.name.toLowerCase().includes(q) || role.description?.toLowerCase().includes(q);
        const matchesStatus = status === 'all' || (status === 'active' ? role.isActive : !role.isActive);
        return matchesText && matchesStatus;
      })
      .sort((a, b) => (normalizeRole(a.name) === 'ORG_ADMIN' ? 0 : 1) - (normalizeRole(b.name) === 'ORG_ADMIN' ? 0 : 1));
  }, [roles, search, status]);

  const { page, setPage, pageSize, total, pageItems } = useClientPagination(filtered, {
    resetKey: `${search}|${status}`,
  });

  const deleteMutation = useMutation({
    mutationFn: (role: ManagedRole) =>
      api.delete<{ success: boolean; data: { message: string } }>(`/roles/${role.id}`),
    onSuccess: () => {
      addToast('Role deleted successfully.', 'success');
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ['managed-roles'] });
    },
    onError: (error: any) => addToast(error.message || 'Unable to delete role.', 'error'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ role, isActive }: { role: ManagedRole; isActive: boolean }) =>
      api.put(`/roles/${role.id}/status`, { isActive }),
    onSuccess: (_data, vars) => {
      addToast(vars.isActive ? `${vars.role.name} activated successfully.` : `${vars.role.name} deactivated successfully.`, 'success');
      setConfirmTarget(null);
      queryClient.invalidateQueries({ queryKey: ['managed-roles'] });
    },
    onError: (err: any) => {
      addToast(err.message || 'Failed to change role status.', 'error');
      setConfirmTarget(null);
    },
  });

  if (isLoading) return <PageLoading />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary">Roles</h1>
          <p className="text-sm text-secondary mt-1">Create organization roles and control their access permissions.</p>
        </div>
        {(isSystemAdmin || hasPermission('roles:create')) && (
          <Button asChild className="gap-2"><Link to="/admin/roles/new"><Plus size={16} />Create Role</Link></Button>
        )}
      </div>

      {isError && (
        <Card>
          <CardContent className="p-4 text-error">
            Unable to load roles: {(error as Error)?.message || 'Check that the Worker is deployed and migration 0018 is applied.'}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={16} />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search roles..." className="pl-9" />
          </div>
          <select
            className="h-10 min-w-0 w-full md:w-auto rounded-lg border bg-surface px-3 text-sm text-primary"
            value={status}
            onChange={(event) => setStatus(event.target.value as typeof status)}
            aria-label="Filter roles by status"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableIndexHead />
                <TableHead>Role</TableHead><TableHead>Permissions</TableHead>
                <TableHead>Users</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((role, index) => {
                const isProtectedRole = normalizeRole(role.name) === 'ORG_ADMIN';
                return (
                <TableRow key={role.id}>
                  <TableIndexCell value={rowSerial(index, page, pageSize)} />
                  <TableCell>
                    <div className="flex items-start gap-2">
                      <ShieldCheck size={18} className="mt-0.5 text-brand-500" />
                      <div><p className="font-medium text-primary">{role.name}</p><p className="text-xs text-secondary">{role.description || 'No description'}</p></div>
                    </div>
                  </TableCell>
                  <TableCell>{role.permissionIds.length}</TableCell>
                  <TableCell>{role.userCount}</TableCell>
                  <TableCell><Badge variant={role.isActive ? 'approved' : 'inactive'}>{role.isActive ? 'Active' : 'Inactive'}</Badge></TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      {isProtectedRole ? (
                        <Badge variant="info">Protected</Badge>
                      ) : (
                        <>
                          {role.isActive ? (
                            <Button variant="ghost" size="sm" className="text-warning hover:text-warning" disabled={statusMutation.isPending && confirmTarget?.item.id === role.id} onClick={() => setConfirmTarget({ item: role, action: 'deactivate' })}>
                              Deactivate
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" className="text-success hover:text-success" disabled={statusMutation.isPending && confirmTarget?.item.id === role.id} onClick={() => setConfirmTarget({ item: role, action: 'activate' })}>
                              Activate
                            </Button>
                          )}
                          <Button asChild variant="ghost" size="sm" title="Edit role" aria-label="Edit role">
                            <Link to={`/admin/roles/${role.id}/edit`}><Pencil size={15} /></Link>
                          </Button>
                          {(isSystemAdmin || hasPermission('roles:delete')) && (
                            <Button
                              type="button" variant="ghost" size="sm"
                              title="Delete role"
                              aria-label="Delete role"
                              onClick={() => setDeleteTarget(role)}
                            >
                              <Trash2 size={15} />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
              {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted">No roles found.</TableCell></TableRow>}
            </TableBody>
          </Table>
          </div>
          <div className="md:hidden space-y-3 p-4">
            {filtered.length === 0 ? (
              <p className="text-center py-8 text-muted">No roles found.</p>
            ) : pageItems.map((role) => {
              const isProtectedRole = normalizeRole(role.name) === 'ORG_ADMIN';
              return (
                <div key={role.id} className="rounded-lg border border-subtle bg-surface p-4 space-y-2 shadow-sm">
                  <div className="flex items-start gap-2">
                    <ShieldCheck size={18} className="mt-0.5 text-brand-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-primary break-words">{role.name}</p>
                      <p className="text-xs text-secondary break-words">{role.description || 'No description'}</p>
                    </div>
                  </div>
                  <dl className="text-sm space-y-1">
                    <div className="flex justify-between gap-3"><dt className="text-secondary shrink-0">Permissions</dt><dd className="text-primary text-right">{role.permissionIds.length}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-secondary shrink-0">Users</dt><dd className="text-primary text-right">{role.userCount}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-secondary shrink-0">Status</dt><dd><Badge variant={role.isActive ? 'approved' : 'inactive'}>{role.isActive ? 'Active' : 'Inactive'}</Badge></dd></div>
                  </dl>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {isProtectedRole ? (
                      <Badge variant="info">Protected</Badge>
                    ) : (
                      <>
                        {role.isActive ? (
                          <Button variant="ghost" size="sm" className="text-warning hover:text-warning min-h-[44px]" disabled={statusMutation.isPending && confirmTarget?.item.id === role.id} onClick={() => setConfirmTarget({ item: role, action: 'deactivate' })}>Deactivate</Button>
                        ) : (
                          <Button variant="ghost" size="sm" className="text-success hover:text-success min-h-[44px]" disabled={statusMutation.isPending && confirmTarget?.item.id === role.id} onClick={() => setConfirmTarget({ item: role, action: 'activate' })}>Activate</Button>
                        )}
                        <Button asChild variant="ghost" size="sm" title="Edit role" aria-label="Edit role" className="min-h-[44px]"><Link to={`/admin/roles/${role.id}/edit`}>Edit</Link></Button>
                        {(isSystemAdmin || hasPermission('roles:delete')) && (
                          <Button type="button" variant="ghost" size="sm" title="Delete role" aria-label="Delete role" className="min-h-[44px]" onClick={() => setDeleteTarget(role)}>Delete</Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {filtered.length > 0 && <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />}
        </CardContent>
      </Card>

      <ConfirmActionDialog
        open={!!confirmTarget && confirmTarget.action === 'deactivate'}
        onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}
        title={`Deactivate ${confirmTarget?.item?.name}?`}
        description="This role will no longer be assignable to users, but existing assignments and audit records will be preserved."
        actionLabel="Deactivate"
        buttonStyle="amber"
        loading={statusMutation.isPending}
        loadingLabel="Deactivating..."
        onConfirm={() => { if (confirmTarget) statusMutation.mutate({ role: confirmTarget.item, isActive: false }); }}
      />

      <ConfirmActionDialog
        open={!!confirmTarget && confirmTarget.action === 'activate'}
        onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}
        title={`Activate ${confirmTarget?.item?.name}?`}
        description="This role will become available for assignment to users."
        actionLabel="Activate"
        buttonStyle="green"
        loading={statusMutation.isPending}
        loadingLabel="Activating..."
        onConfirm={() => { if (confirmTarget) statusMutation.mutate({ role: confirmTarget.item, isActive: true }); }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role?</AlertDialogTitle>
            <AlertDialogDescription>
              Role: {deleteTarget?.name}
              <br />
              Assigned users: {deleteTarget?.userCount ?? 0}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <p className="text-sm text-secondary">
            This permanently deletes the role and removes it from all assigned users. Users will not be deleted.
          </p>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
